import React, { useState, useEffect } from 'react';
import { Bot, Play, Pause, Settings, Clock, CheckCircle, AlertCircle, Image as ImageIcon, Activity } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import AgentStyleSelector from '../components/AgentStyleSelector';

const Agent = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agentConfig, setAgentConfig] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);

  // Load agent configuration and status
  useEffect(() => {
    loadAgentData();
  }, []);

  const loadAgentData = async () => {
    try {
      setLoading(true);
      
      // Load agent configuration
      const configResponse = await fetch('/api/agent/config', {
        credentials: 'include'
      });
      
      if (!configResponse.ok) {
        throw new Error('Failed to load agent configuration');
      }
      
      const configData = await configResponse.json();
      
      // Handle backward compatibility: if only single style exists, convert to array
      if (configData.metadataPreferences && !configData.metadataPreferences.styles && configData.metadataPreferences.style) {
        configData.metadataPreferences.styles = [configData.metadataPreferences.style];
      }
      
      setAgentConfig(configData);
      
      // Load agent status
      const statusResponse = await fetch('/api/agent/status', {
        credentials: 'include'
      });
      
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setAgentStatus(statusData);
      }
      
    } catch (error) {
      console.error('Error loading agent data:', error);
      toast.error('Failed to load agent configuration');
    } finally {
      setLoading(false);
    }
  };


  const handleToggleAgent = async () => {
    try {
      setSaving(true);
      const newEnabled = !agentConfig.enabled;
      
      const response = await fetch('/api/agent/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ...agentConfig,
          enabled: newEnabled
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update agent configuration');
      }
      
      const updatedConfig = await response.json();
      setAgentConfig(updatedConfig);
      
      toast.success(newEnabled ? 'Agent enabled' : 'Agent disabled');
      
      // Reload status after toggling
      await loadAgentData();
      
    } catch (error) {
      console.error('Error toggling agent:', error);
      toast.error('Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      
      const response = await fetch('/api/agent/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(agentConfig)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save agent configuration');
      }
      
      const updatedConfig = await response.json();
      setAgentConfig(updatedConfig);
      
      toast.success('Agent configuration saved');
      
    } catch (error) {
      console.error('Error saving agent configuration:', error);
      toast.error('Failed to save agent configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    try {
      setSaving(true);
      
      const response = await fetch('/api/agent/run', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to run agent');
      }
      
      const result = await response.json();
      
      if (result.newPhotos === 0) {
        toast.success('No new photos found to process');
      } else if (result.photosNeedingMetadata === 0) {
        toast.success(`Found ${result.newPhotos} new photos, but all already have metadata`);
      } else if (result.jobCreated) {
        toast.success(`Agent started processing ${result.photosNeedingMetadata} photos needing metadata`);
      } else if (result.requiresPayment) {
        toast.success(`Found ${result.photosNeedingMetadata} photos needing metadata, but payment required`);
      } else {
        toast.success(`Found ${result.newPhotos} new photos`);
      }
      
      // Reload status
      await loadAgentData();
      
    } catch (error) {
      console.error('Error running agent:', error);
      toast.error(error.message || 'Failed to run agent');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setAgentConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateFilters = (field, value) => {
    setAgentConfig(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [field]: value
      }
    }));
  };

  const updateMetadataPreferences = (field, value) => {
    setAgentConfig(prev => ({
      ...prev,
      metadataPreferences: {
        ...prev.metadataPreferences,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!agentConfig) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load agent</h3>
        <p className="mt-1 text-sm text-gray-500">Please try refreshing the page.</p>
      </div>
    );
  }

  const connectedProviders = user?.connectedAccounts?.map(acc => acc.provider) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Bot className={`h-12 w-12 ${agentConfig.enabled ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900">Photo Processing Agent</h1>
              <p className="mt-1 text-sm text-gray-500">
                Automatically scan for new photos and apply AI metadata
              </p>
            </div>
          </div>
          <button
            onClick={handleToggleAgent}
            disabled={saving}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm ${
              agentConfig.enabled
                ? 'text-white bg-red-600 hover:bg-red-700'
                : 'text-white bg-green-600 hover:bg-green-700'
            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50`}
          >
            {agentConfig.enabled ? (
              <>
                <Pause className="mr-2 h-4 w-4" />
                Disable Agent
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Enable Agent
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Card */}
      {agentStatus && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Activity className="mr-2 h-5 w-5" />
            Agent Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="mt-1 text-lg font-semibold text-gray-900">
                    {agentConfig.enabled ? 'Active' : 'Inactive'}
                  </p>
                </div>
                {agentConfig.enabled ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <Pause className="h-8 w-8 text-gray-400" />
                )}
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div>
                <p className="text-sm text-gray-500">Last Scan</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {agentStatus.lastScanAt 
                    ? new Date(agentStatus.lastScanAt).toLocaleString()
                    : 'Never'}
                </p>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div>
                <p className="text-sm text-gray-500">Photos Processed</p>
                <p className="mt-1 text-lg font-semibold text-gray-900">
                  {agentStatus.totalPhotosProcessed || 0}
                </p>
              </div>
            </div>
          </div>
          
          {agentStatus.nextScanAt && (
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <Clock className="mr-2 h-4 w-4" />
              Next scan scheduled for {new Date(agentStatus.nextScanAt).toLocaleString()}
            </div>
          )}
          
          <div className="mt-4">
            <button
              onClick={handleRunNow}
              disabled={saving || !agentConfig.enabled}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <Play className="mr-2 h-4 w-4" />
              Run Now
            </button>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <Settings className="mr-2 h-5 w-5" />
          Agent Configuration
        </h2>
        
        <div className="space-y-6">
          {/* Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo Provider
            </label>
            <select
              value={agentConfig.provider || ''}
              onChange={(e) => updateConfig('provider', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Select a provider</option>
              {connectedProviders.map(provider => (
                <option key={provider} value={provider}>
                  {provider.charAt(0).toUpperCase() + provider.slice(1)}
                </option>
              ))}
            </select>
            {connectedProviders.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                No photo providers connected. Connect a provider in Settings.
              </p>
            )}
          </div>

          {/* Scan Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scan Frequency
            </label>
            <select
              value={agentConfig.scanFrequency || 'daily'}
              onChange={(e) => updateConfig('scanFrequency', e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="every5minutes">Every 5 Minutes</option>
              <option value="every15minutes">Every 15 Minutes</option>
              <option value="every30minutes">Every 30 Minutes</option>
              <option value="hourly">Every Hour</option>
              <option value="every6hours">Every 6 Hours</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {/* Filters */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Photo Filters</h3>
            
            {/* Tags */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">
                Tags (comma-separated, optional)
              </label>
              <input
                type="text"
                value={agentConfig.filters?.tags?.join(', ') || ''}
                onChange={(e) => {
                  const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                  updateFilters('tags', tags);
                }}
                placeholder="vacation, landscape, sunset"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Metadata Preferences */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Metadata Preferences</h3>
            
            <AgentStyleSelector
              selectedStyles={
                agentConfig.metadataPreferences?.styles || 
                (agentConfig.metadataPreferences?.style ? [agentConfig.metadataPreferences.style] : ['artistic'])
              }
              setSelectedStyles={(styles) => updateMetadataPreferences('styles', styles)}
              descriptionSentences={agentConfig.metadataPreferences?.descriptionSentences || 3}
              setDescriptionSentences={(sentences) => updateMetadataPreferences('descriptionSentences', sentences)}
            />
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t">
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Save Configuration
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ImageIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">How the Agent Works</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>The agent scans your photo provider at the specified frequency</li>
                <li>It identifies new photos uploaded since the last scan</li>
                <li>Only processes photos missing titles or descriptions (won't overwrite existing metadata)</li>
                <li>Matching photos are automatically processed with your preferred AI style</li>
                <li>Generated metadata is automatically applied back to your photos on {user?.connectedAccounts?.[0]?.provider || 'your provider'}</li>
                <li>Everything happens automatically - no manual steps needed</li>
                <li>You can view processed photos in Job History</li>
                <li>Free trial photos will be used before paid processing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agent;

